import {ExperimentNames} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment, useExperiments, useTranslation} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useEffect, useState} from 'react'
import {
  getCurrentLanguage,
  getLanguage,
  isPreview,
  isPrimaryLanguage,
  isSite,
} from '../../../../../../commons/selectors/environment'
import {
  getInstance,
  getInstanceId,
  getMetaSiteId,
  getSiteOwnerId,
  getVisitorId,
} from '../../../../../../commons/selectors/instance'
import {useLogger} from '../../../hooks/logger'
import {useAppActions, useAppHost, useAppSelector} from '../../../hooks/runtime'
import {isCurrentUserLoggedIn} from '../../../selectors/current-user'
import {getEvent} from '../../../selectors/event'
import {isCheckoutActionPending} from '../../../selectors/pending-requests'
import {getPlacedOrder} from '../../../selectors/placed-order'
import {getReservationId} from '../../../selectors/reservation'
import {isCheckoutVisibilityEnabled} from '../../../selectors/checkout-visibility'
import {CheckoutPaymentPath} from '../../../services/checkout-visibility'
import {Interaction} from '../../../services/logger'
import {FormButton} from '../../form-button/form-button'
import {ExpressCheckout} from './express-checkout/express-checkout'
import {PaymentProps} from './interfaces'
import {PaymentWidget} from './payment-widget'
import s from './payment.scss'
import {classes} from './paymentsStyleApi.st.css'

const PAYMENT_METHODS_WITHOUT_FEE = ['offline']

export const Payment = ({paymentRef, editing, onSubmit}: PaymentProps) => {
  const logger = useLogger()
  const {experiments} = useExperiments()
  const {isMobile} = useEnvironment()
  const {t} = useTranslation()
  const {
    openCantCompletePaymentModal,
    navigateToOrder,
    navigateToOrderUrl,
    expressCheckout,
    updatePaymentMethod,
    reportCheckoutVisibilityStage,
    setCheckoutVisibilityPaymentPath,
  } = useAppActions()
  const [currentPaymentMethodId, setCurrentPaymentMethodId] = useState('')
  const [paymentInProgress, setPaymentInProgress] = useState(false)

  const host = useAppHost()
  const event = useAppSelector(getEvent)
  const order = useAppSelector(getPlacedOrder)
  const reservationId = useAppSelector(getReservationId)
  const visibilityEnabled = useAppSelector(isCheckoutVisibilityEnabled)
  const preview = useAppSelector(isPreview)
  const locale = useAppSelector(getLanguage)
  const primaryLanguage = useAppSelector(isPrimaryLanguage)
  const site = useAppSelector(isSite)
  const currentLanguage = useAppSelector(getCurrentLanguage)
  const instanceId = useAppSelector(getInstanceId)
  const visitorId = useAppSelector(getVisitorId)
  const msid = useAppSelector(getMetaSiteId)
  const siteOwnerId = useAppSelector(getSiteOwnerId)
  const instance = useAppSelector(getInstance)
  const currentUserLoggedIn = useAppSelector(isCurrentUserLoggedIn)
  const updateOrderInProgress = useAppSelector(isCheckoutActionPending)

  const expressCheckoutEnabled = experiments.enabled(ExperimentNames.ExpressCheckout)
  const updatePaymentMethodEnabled = experiments.enabled(ExperimentNames.UpdatePaymentMethodEnabled)

  const loading = (updatePaymentMethodEnabled && updateOrderInProgress) || paymentInProgress
  const selectedPaymentMethodWithoutFee =
    updatePaymentMethodEnabled && PAYMENT_METHODS_WITHOUT_FEE.includes(currentPaymentMethodId)
  const expressCheckoutVisible = expressCheckoutEnabled && !selectedPaymentMethodWithoutFee

  const onPaymentMethodChanged = (paymentMethodId: string) => {
    setCurrentPaymentMethodId(paymentMethodId)

    if (visibilityEnabled) {
      setCheckoutVisibilityPaymentPath(
        PAYMENT_METHODS_WITHOUT_FEE.includes(paymentMethodId)
          ? CheckoutPaymentPath.Offline
          : CheckoutPaymentPath.Online,
      )
    }

    if (updatePaymentMethodEnabled && !preview) {
      updatePaymentMethod({paymentMethod: paymentMethodId})
    }
  }

  useEffect(() => {
    logger.startInteraction(Interaction.PaymentWidgetLoad)
  }, [])

  return (
    <div className={classNames(s.container, classes.paymentsStyleApi)}>
      <>
        {expressCheckoutVisible ? (
          <ExpressCheckout
            locale={locale}
            msid={msid}
            visitorId={visitorId}
            instanceId={instanceId}
            siteOwnerId={siteOwnerId}
            instance={instance}
            expressCheckout={expressCheckout}
          />
        ) : null}
        <PaymentWidget
          locale={locale}
          ref={paymentRef}
          eventId={event.id}
          snapshotId={order?.snapshotId}
          reservationId={reservationId}
          isMobile={isMobile}
          isPreview={preview}
          openCantCompletePaymentModal={openCantCompletePaymentModal}
          navigateToOrder={navigateToOrder}
          navigateToOrderUrl={navigateToOrderUrl}
          isSite={site}
          visibilityEnabled={visibilityEnabled}
          visitorId={visitorId}
          msid={msid}
          instanceId={instanceId}
          primaryLanguage={primaryLanguage}
          siteOwnerId={siteOwnerId}
          externalSubmitButton
          order={order}
          lang={currentLanguage}
          instance={instance}
          siteStyles={host.style}
          isSignedInUser={currentUserLoggedIn}
          updateLayout={host.updateLayout}
          logger={logger}
          reportCheckoutVisibilityStage={reportCheckoutVisibilityStage}
          setCheckoutVisibilityPaymentPath={setCheckoutVisibilityPaymentPath}
          onPaymentMethodChanged={onPaymentMethodChanged}
          currentPaymentMethodId={currentPaymentMethodId}
        />
      </>
      {editing && (
        <div className={s.button}>
          <FormButton
            spinner={loading}
            disabled={loading}
            dataHook={DH.PAYMENT_METHOD_SUBMIT}
            type="submit"
            text={t('mobile.checkout.placeOrder')}
            onClick={async () => {
              setPaymentInProgress(true)
              onSubmit()
                .then(() => {
                  setTimeout(() => {
                    setPaymentInProgress(false) // wait for navigation
                  }, 1000)
                })
                .catch(() => {
                  setPaymentInProgress(false)
                })
            }}
          />
        </div>
      )}
    </div>
  )
}
